/**
 * Student specialties catalogue API.
 */

import { apiClient } from './api-client';
import type { PaginatedResponse } from '@/types/data-table.types';
import type { Level } from '@/types/level.types';
import type {
  Specialty,
  CreateSpecialtyDto,
  UpdateSpecialtyDto,
} from '@/types/specialty.types';

/** `GET /specialties` */
export const getSpecialties = async (activeOnly = true): Promise<Specialty[]> => {
  const { data } = await apiClient.get<Specialty[] | PaginatedResponse<Specialty>>('/specialties', {
    params: { activeOnly },
  });
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

/** `GET /specialties/:id` */
export const getSpecialty = async (id: string): Promise<Specialty> => {
  const response = await apiClient.get<Specialty>(`/specialties/${id}`);
  return response.data;
};

/** `POST /specialties` */
export const createSpecialty = async (dto: CreateSpecialtyDto): Promise<Specialty> => {
  const response = await apiClient.post<Specialty>('/specialties', dto);
  return response.data;
};

/** `PATCH /specialties/:id` */
export const updateSpecialty = async (id: string, dto: UpdateSpecialtyDto): Promise<Specialty> => {
  const response = await apiClient.patch<Specialty>(`/specialties/${id}`, dto);
  return response.data;
};

/** `DELETE /specialties/:id` */
export const deleteSpecialty = async (id: string): Promise<void> => {
  await apiClient.delete(`/specialties/${id}`);
};

/** `GET /specialties/levels/high` */
export const getHighSchoolLevels = async (): Promise<Level[]> => {
  const response = await apiClient.get<Level[]>('/specialties/levels/high');
  return response.data;
};

/** `PATCH /specialties/reorder` */
export const reorderSpecialties = async (ids: string[]): Promise<void> => {
  await apiClient.patch('/specialties/reorder', { ids });
};
